import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import api from '../lib/api';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const pollInterval = 2000;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) { setStatus('error'); return; }

    let attempt = 0;
    let stopped = false;

    const poll = async () => {
      if (stopped || attempt >= maxAttempts) { if (!stopped) setStatus('error'); return; }
      attempt++;
      setAttempts(attempt);
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        const data = response.data;
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          stopped = true;
          setStatus('success');
          if (refreshUser) await refreshUser();
          return;
        }
        if (data.status === 'expired') { setStatus('error'); return; }
      } catch (e) { console.error('Payment check error:', e); }
      if (!stopped) setTimeout(poll, pollInterval);
    };
    poll();

    return () => { stopped = true; };
  }, [searchParams]);

  const getPackageLabel = (packageId) => {
    const labels = {
      'starter': 'Starter',
      'pro': 'Pro',
      'enterprise': 'Tout-en-un'
    };
    return labels[packageId] || packageId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-slate-200">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Vérification du paiement...
              </h1>
              <p className="text-slate-600 mb-4">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
              <div className="text-sm text-slate-400">
                Tentative {attempts + 1}/{maxAttempts}
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Paiement réussi !
              </h1>
              <p className="text-slate-600 mb-6">
                Merci pour votre abonnement. Votre compte est maintenant activé.
              </p>
              
              {paymentData && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500">Plan</span>
                    <span className="font-medium text-slate-900">
                      {getPackageLabel(paymentData.package_id)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Montant</span>
                    <span className="font-medium text-slate-900">
                      {paymentData.amount} {paymentData.currency}
                    </span>
                  </div>
                </div>
              )}

              <Button 
                onClick={() => navigate('/dashboard')} 
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Accéder à mon espace
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Problème de paiement
              </h1>
              <p className="text-slate-600 mb-6">
                Nous n'avons pas pu confirmer votre paiement. Si le montant a été débité, 
                contactez notre support.
              </p>
              <div className="flex gap-3">
                <Button 
                  onClick={() => navigate('/pricing')} 
                  variant="outline"
                  className="flex-1"
                >
                  Réessayer
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Dashboard
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Support : disponible via le systeme de tickets interne
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
